import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookMarked, Trash2, Plus, Users, User, Zap, Star } from 'lucide-react';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../ui/jewel-modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

/**
 * Inline template picker + Save-as-template button. Managers can save frequently-used
 * compositions and pick them with one tap in the compose modal.
 */
export const TemplateLibrary = ({ builtInTemplates = [], currentForm, onApply }) => {
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hr-notices/templates/custom`, { headers: h });
      setCustomTemplates(r.data.templates || []);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const applyTemplate = async (tpl) => {
    onApply && onApply({
      type: tpl.type,
      title: tpl.title || tpl.name,
      description: tpl.description || '',
      priority: tpl.priority || 'normal',
    });
    if (tpl.id) {
      // Track usage
      try {
        const h = getAuthHeaders();
        await axios.post(`${BACKEND_URL}/api/hr-notices/templates/custom/${tpl.id}/use`, {}, { headers: h });
      } catch {}
    }
    toast.success(`Loaded: ${tpl.name || tpl.label}`);
  };

  const saveCurrent = async () => {
    if (!saveName.trim()) { toast.error('Name your template'); return; }
    if (!currentForm.type) { toast.error('Pick a notice type first'); return; }
    setLoading(true);
    try {
      const h = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/hr-notices/templates/custom`, {
        name: saveName.trim(),
        type: currentForm.type,
        title: currentForm.title || '',
        description: currentForm.description || '',
        priority: currentForm.priority || 'normal',
        is_shared: saveShared,
      }, { headers: h });
      toast.success('Template saved');
      setShowSave(false);
      setSaveName('');
      setSaveShared(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setLoading(false);
  };

  const removeTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const h = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/hr-notices/templates/custom/${id}`, { headers: h });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const hasContent = currentForm.type && (currentForm.title || currentForm.description);

  return (
    <div className="p-4 rounded-2xl bg-gradient-to-br from-violet-500/[0.04] to-cyan-500/[0.03] border border-white/[0.06] space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BookMarked className="w-3.5 h-3.5 text-violet-300" />
          <p className="text-[10px] tracking-[0.2em] uppercase text-violet-200/80 font-semibold">Templates</p>
          {customTemplates.length > 0 && (
            <span className="text-white/40 text-[10px]">· {customTemplates.length} saved</span>
          )}
        </div>
        <button type="button" onClick={() => setShowSave(true)} disabled={!hasContent}
          data-testid="template-save-btn"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-500/[0.10] border border-violet-500/25 text-violet-200 hover:bg-violet-500/[0.20] disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-medium transition-all">
          <Plus className="w-2.5 h-2.5" /> Save current as template
        </button>
      </div>

      {/* Custom templates row */}
      {customTemplates.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[9px] tracking-[0.14em] uppercase text-white/35 font-bold flex items-center gap-1.5">
            <Star className="w-2.5 h-2.5 text-amber-300" /> Your saved templates
          </p>
          <div className="flex flex-wrap gap-1.5">
            {customTemplates.slice(0, 12).map(t => (
              <div key={t.id} className="group inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] hover:border-violet-400/40 transition-all">
                <button type="button" onClick={() => applyTemplate(t)}
                  data-testid={`template-apply-${t.id}`}
                  className="text-[11px] text-white/85 hover:text-white font-medium flex items-center gap-1.5">
                  <Zap className="w-2.5 h-2.5 text-amber-300" />
                  <span>{t.name}</span>
                  {t.is_shared ? <Users className="w-2.5 h-2.5 text-cyan-300/60" title="Shared" /> : <User className="w-2.5 h-2.5 text-white/30" />}
                  {t.usage_count > 0 && <span className="text-white/35 text-[9px]">·{t.usage_count}</span>}
                </button>
                <button type="button" onClick={() => removeTemplate(t.id)}
                  data-testid={`template-delete-${t.id}`}
                  className="w-4 h-4 rounded-full flex items-center justify-center text-white/40 hover:text-rose-300 hover:bg-rose-500/20 opacity-0 group-hover:opacity-100 transition-all">
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save-as-template modal */}
      {showSave && (
        <JewelModal open={showSave} onOpenChange={setShowSave} icon={BookMarked} title="Save as template" subtitle="Reuse this composition in seconds" accent="violet" size="md" testid="save-template-modal"
          footer={<>
            <JewelButton variant="ghost" onClick={() => setShowSave(false)}>Cancel</JewelButton>
            <JewelButton accent="violet" onClick={saveCurrent} loading={loading} disabled={loading || !saveName.trim()} icon={BookMarked} data-testid="save-template-confirm">Save template</JewelButton>
          </>}>
          <div className="space-y-3">
            <JewelField label="Template name" required>
              <JewelInput value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Standard sickness self-cert reply"
                data-testid="save-template-name" autoFocus />
            </JewelField>
            <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05] space-y-1.5">
              <p className="text-[9px] tracking-[0.14em] uppercase text-white/40">You will save</p>
              <p className="text-white/80 text-sm">Type: <span className="text-cyan-300">{currentForm.type || '—'}</span></p>
              <p className="text-white/80 text-sm">Title: <span className="text-white">{currentForm.title || <span className="italic text-white/40">empty</span>}</span></p>
              <p className="text-white/60 text-xs line-clamp-3">{currentForm.description || <span className="italic text-white/40">empty description</span>}</p>
            </div>
            <label className="inline-flex items-center gap-2 text-white/70 text-xs cursor-pointer">
              <input type="checkbox" checked={saveShared} onChange={(e) => setSaveShared(e.target.checked)} className="w-4 h-4 accent-violet-400" data-testid="save-template-shared" />
              <Users className="w-3 h-3" /> Share with other managers
            </label>
          </div>
        </JewelModal>
      )}
    </div>
  );
};

export default TemplateLibrary;
